import { computed, action, observable } from 'mobx';
import { StoreBase } from '../../../common/StoreBase';
import { IMfaSetupConfiguration } from '../models';
import { IRequestOptions } from '@kurtosys/ksys-api-client/dist/models/IRequestOptions';
import { TMfaDisableRequestBody } from '@kurtosys/ksys-api-client/dist/models/requests/auth/TMfaDisableRequestBody';
import { IDrilldownRow } from '../models/IDrilldownRow';
import { ICheckboxRow } from '../models/ICheckboxRow';
import { MfaMethods } from '../models/MfaMethods';
import { MfaStep } from '../models/MfaStep';
import { IConfirmDialogProps } from '@kurtosys/ksys-app-components/dist/components/base/ConfirmDialog/models';
import { TDisplayType } from '../models/TDisplayType';

export class MfaSetupStore extends StoreBase {
	static componentKey: 'mfaSetup' = 'mfaSetup';
	@observable step: MfaStep = MfaStep.default;
	@observable setupPushEnabled = false;
	@observable setupTextEnabled = false;
	@observable setupCallEnabled = false;
	@observable showConfirmDialog = false;
	@observable confirmDialogProps: (IConfirmDialogProps | null) = null;

	@computed
	get isPushEnabled(): boolean {
		return this.enabled2FAMethods.includes(MfaMethods.push);
	}

	@computed
	get isTextEnabled(): boolean {
		return this.enabled2FAMethods.includes(MfaMethods.text);
	}

	@computed
	get isCallEnabled(): boolean {
		return this.enabled2FAMethods.includes(MfaMethods.call);
	}

	@computed
	get configuration(): IMfaSetupConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(MfaSetupStore.componentKey);
		}
	}

	@computed
	get userPhone(): string | undefined {
		const { appStore } = this.storeContext;
		const phone = appStore && appStore.phone;
		if (phone) {
			return this.obfuscatePhone(phone);
		}
	}

	@computed
	get enabled2FAMethods(): string[] {
		const { appStore } = this.storeContext;
		return appStore && appStore.enabled2FAMethods;
	}

	@computed
	get displayType(): TDisplayType {
		return this.configuration && this.configuration.displayType || 'checkbox';
	}

	@computed
	get mfaDrilldownRows(): IDrilldownRow[] {
		const rows: IDrilldownRow[] = [];
		// Push
		if (this.setupPushEnabled) {
			rows.push({
				label: this.isPushEnabled ? 'Push Device Registered' : 'Activate Push Authentication',
				buttonText: this.isPushEnabled ? 'DISABLE' : 'ENABLE',
				onClick: this.pushOnClick,
			});
		}
		// Text
		if (this.setupTextEnabled) {
			rows.push({
				label: this.isTextEnabled
					? 'Text Authentication Enabled - (' + this.userPhone + ')'
					: 'Activate Text Authentication',
				buttonText: this.isTextEnabled ? 'DISABLE' : 'ENABLE',
				onClick: this.textOnClick,
			});
		}
		// Call
		if (this.setupCallEnabled) {
			rows.push({
				label: this.isCallEnabled
					? 'Call Authentication Enabled - (' + this.userPhone + ')'
					: 'Activate Call Authentication',
				buttonText: this.isCallEnabled ? 'DISABLE' : 'ENABLE',
				onClick: this.callOnClick,
			});
		}
		return rows;
	}

	@computed
	get mfaCheckboxRows(): ICheckboxRow[] {
		const rows: ICheckboxRow[] = [];
		const checkboxLabels = this.configuration && this.configuration.checkboxLabels;

		// Push
		if (this.setupPushEnabled) {
			rows.push({
				method: MfaMethods.push,
				label: checkboxLabels && checkboxLabels.push || 'Push Authentication',
				onChange: this.pushOnChange,
				activeRow: this.step === MfaStep.pushSetup,
				checked: this.isPushEnabled,
			});
		}
		// Text
		if (this.setupTextEnabled) {
			rows.push({
				method: MfaMethods.text,
				label: checkboxLabels && checkboxLabels.text || 'Text Authentication',
				onChange: this.textOnChange,
				activeRow: this.step === MfaStep.textSetup,
				checked: this.isTextEnabled,
			});
		}
		// Call
		if (this.setupCallEnabled) {
			rows.push({
				method: MfaMethods.call,
				label: checkboxLabels && checkboxLabels.call || 'Call Authentication',
				onChange: this.callOnChange,
				activeRow: this.step === MfaStep.callSetup,
				checked: this.isCallEnabled,
			});
		}
		return rows;
	}

	@computed
	get confirmPushDisableHeading(): string {
		return (this.configuration && this.configuration.confirmPushDisableHeading) ||
			'Are you sure you want to disable Two-Factor Authentication';
	}

	@computed
	get confirmPushDisableMessage(): string {
		return (this.configuration && this.configuration.confirmPushDisableMessage) ||
			'This will disable Push Authentication for your account. To re-enable Push Authentication, you will have to re-register your device.';
	}

	@computed
	get confirmTextDisableHeading(): string {
		return (this.configuration && this.configuration.confirmTextDisableHeading) ||
			'Are you sure you want to disable Two-Factor Authentication';
	}

	@computed
	get confirmTextDisableMessage(): string {
		return (this.configuration && this.configuration.confirmTextDisableMessage) ||
			'This will disable Text Authentication for your account. To re-enable Text Authentication, you will have to re-register your device.';
	}

	@computed
	get confirmCallDisableHeading(): string {
		return (this.configuration && this.configuration.confirmPushDisableHeading) ||
			'Are you sure you want to disable Two-Factor Authentication';
	}

	@computed
	get confirmCallDisableMessage(): string {
		return (this.configuration && this.configuration.confirmCallDisableMessage) ||
			'This will disable Call Authentication for your account. To re-enable Call Authentication, you will have to re-register your device.';
	}

	@action
	async initialize(): Promise<void> {
		const { mfaSetupPushStore } = this.storeContext;
		this.getSetupMfaSettings();
		mfaSetupPushStore.initialize();
	}

	@action
	getSetupMfaSettings = async () => {
		// TODO: Apply check for whether MFA settings are enabled per application
		this.setupPushEnabled = true;
		this.setupTextEnabled = true;
		this.setupCallEnabled = true;

	}

	@action confirmDisablePush = () => {
		this.confirmDialogProps = {
			heading: this.confirmPushDisableHeading,
			message: this.confirmPushDisableMessage,
			onConfirm: () => this.mfaDisable(MfaMethods.push),
			onCancel: this.cancelConfirmDialog,
			isOpen: true,
		};
		this.showConfirmDialog = true;
	}

	@action confirmDisableText = () => {
		this.confirmDialogProps = {
			heading: this.confirmTextDisableHeading,
			message: this.confirmTextDisableMessage,
			onConfirm: () => this.mfaDisable(MfaMethods.text),
			onCancel: this.cancelConfirmDialog,
			isOpen: true,
		};
		this.showConfirmDialog = true;
	}

	@action confirmDisableCall = () => {
		this.confirmDialogProps = {
			heading: this.confirmCallDisableHeading,
			message: this.confirmCallDisableMessage,
			onConfirm: () => this.mfaDisable(MfaMethods.call),
			onCancel: this.cancelConfirmDialog,
			isOpen: true,
		};
		this.showConfirmDialog = true;
	}

	@action pushOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event && event.target && event.target.checked;
		if (value && !this.isPushEnabled) {
			this.setStep(MfaStep.pushSetup);
		}
		else {
			if (this.isPushEnabled) {
				this.confirmDisablePush();
			}
			else {
				this.setStep(MfaStep.default);
			}
		}
	}

	@action textOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event && event.target && event.target.checked;
		if (value && !this.isTextEnabled) {
			this.setStep(MfaStep.textSetup);
		}
		else {
			if (this.isTextEnabled) {
				this.confirmDisableText();
			}
			else {
				this.setStep(MfaStep.default);
			}
		}
	}

	@action callOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event && event.target && event.target.checked;
		if (value && !this.isCallEnabled) {
			this.setStep(MfaStep.callSetup);
		}
		else {
			if (this.isCallEnabled) {
				this.confirmDisableCall();
			}
			else {
				this.setStep(MfaStep.default);
			}
		}
	}

	@action pushOnClick = () => {
		if (this.isPushEnabled) {
			this.confirmDisablePush();
		}
		else {
			this.setStep(MfaStep.pushSetup);
		}
	}

	@action textOnClick = () => {
		if (this.isTextEnabled) {
			this.confirmDisableText();
		}
		else {
			this.setStep(MfaStep.textSetup);
		}
	}

	@action callOnClick = () => {
		if (this.isCallEnabled) {
			this.confirmDisableCall();
		}
		else {
			this.setStep(MfaStep.callSetup);
		}
	}

	@action
	setStep = async (step: MfaStep) => {
		if (step === MfaStep.pushSetup && this.setupPushEnabled) {
			this.step = step;
		}
		else if (step === MfaStep.callSetup && this.setupCallEnabled) {
			this.step = step;
		}
		else if (step === MfaStep.textSetup && this.setupTextEnabled) {
			this.step = step;
		}
		else if (step === MfaStep.default) {
			this.step = step;
		}
	}

	@action finishSetup = async () => {
		const { appStore } = this.storeContext;
		await appStore.refreshUser();
		this.setStep(MfaStep.default);
	}

	@action
	cancelConfirmDialog = () => {
		this.showConfirmDialog = false;
	}

	@action
	mfaDisable = async (type: MfaMethods) => {
		const { kurtosysApiStore, appStore } = this.storeContext;
		const overrideOptions: Partial<IRequestOptions<TMfaDisableRequestBody>> = {
			body: {
				type,
			},
		};
		try {
			const response = await kurtosysApiStore.mfaDisable.callApi(overrideOptions);
			if (response.ok) {
				await appStore.refreshUser();
				return;
			}
		}
		catch (ex) {
		}
		this.showConfirmDialog = false;
	}

	obfuscatePhone(phone: string) {
		if (!phone) {
			return '';
		}
		const length = phone.length;
		return phone.substr(length - 3, 3).padStart(length, '*');
	}
}